import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MyModal } from '@shared/ui/my-modal';
import { Button } from '@shared/ui/button';
import { Select, SelectOption } from '@shared/ui/select';
import { useAppDispatch } from '../../../app/store/store';
import { setErrorNotification, setNotification } from '../../../app/store/notifications';
import { asList, diagnosticsApi } from '../../../api/services/diagnostics.service';
import {
  CONDITION_LABELS,
  DEFECT_LABELS,
  DISPOSITION_LABELS,
  RESPONSIBLE_LABELS,
  WARRANTY_LABELS,
} from '../../../types/diagnosticsInterfaces';
import type {
  Condition,
  DefectType,
  DeviceResultPayload,
  DiagnosticDevice,
  Disposition,
  FaultReason,
  Housing,
  ResponsibleParty,
  WarrantyStatus,
} from '../../../types/diagnosticsInterfaces';

interface Props {
  ticketId: number;
  device: DiagnosticDevice | null;
  onClose: () => void;
  onSaved: (d: DiagnosticDevice) => void;
}

interface FormValues {
  warranty_status: WarrantyStatus;
  condition: Condition;
  disposition: Disposition;
  responsible_party: ResponsibleParty;
  defect_type: DefectType;
  housing: SelectOption | null;
  manualReason: boolean;
  fault_reason: SelectOption | null;
  fault_reason_text: string;
  manufactured_at: string;
  engineer_note: string;
}

const FIELD_LABEL = 'block text-xs font-medium text-gray-500 mb-1';
const INPUT =
  'w-full h-[40px] px-3 rounded-lg border border-gray-200 text-sm text-[#2d2c2c] focus:outline-none focus:border-gray-400';
const selectStyles = { control: () => ({ height: '40px', minHeight: '40px' }) };

const toOptions = <K extends string>(labels: Record<K, string>): SelectOption[] =>
  (Object.entries(labels) as [K, string][]).map(([value, label]) => ({ value, label }));

/** Селект по enum'у поверх react-hook-form Controller. */
function EnumField<K extends string>({
  control,
  name,
  label,
  labels,
}: {
  control: any;
  name: keyof FormValues;
  label: string;
  labels: Record<K, string>;
}) {
  const options = useMemo(() => toOptions(labels), [labels]);
  return (
    <div>
      <label className={FIELD_LABEL}>{label}</label>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            isVisible={false}
            isClearable={false}
            customStyles={selectStyles}
            options={options}
            optionSelected={options.find((o) => o.value === field.value) ?? null}
            onSelected={(o) => field.onChange(o?.value ?? field.value)}
          />
        )}
      />
    </div>
  );
}

export function DeviceResultModal({ ticketId, device, onClose, onSaved }: Props) {
  const dispatch = useAppDispatch();
  const [housings, setHousings] = useState<Housing[]>([]);
  const [reasons, setReasons] = useState<FaultReason[]>([]);
  const [saving, setSaving] = useState(false);

  const { control, register, handleSubmit, reset, watch, setValue } = useForm<FormValues>();
  const manualReason = watch('manualReason');

  useEffect(() => {
    if (!device) return;
    diagnosticsApi.getHousings().then((r) => setHousings(asList(r.data))).catch(() => setHousings([]));
    diagnosticsApi.getFaultReasons().then((r) => setReasons(asList(r.data))).catch(() => setReasons([]));
    reset({
      warranty_status: device.warranty_status,
      condition: device.condition,
      disposition: device.disposition,
      responsible_party: device.responsible_party,
      defect_type: device.defect_type,
      housing: null, // выставим, когда придут корпуса
      manualReason: !device.fault_reason && Boolean(device.fault_reason_text),
      fault_reason: device.fault_reason
        ? { value: device.fault_reason, label: device.reason_display }
        : null,
      fault_reason_text: device.fault_reason_text ?? '',
      manufactured_at: device.manufactured_at ?? '',
      engineer_note: device.engineer_note ?? '',
    });
  }, [device, reset]);

  useEffect(() => {
    if (!device?.housing || !housings.length) return;
    const h = housings.find((x) => x.id === device.housing);
    if (h) setValue('housing', { value: h.id, label: h.name });
  }, [device, housings, setValue]);

  const housingOptions = useMemo(
    () => housings.map((h) => ({ value: h.id, label: h.name })),
    [housings],
  );
  const reasonOptions = useMemo(
    () => reasons.map((r) => ({ value: r.id, label: r.text })),
    [reasons],
  );

  const onSubmit = handleSubmit(async (v) => {
    if (!device) return;
    const payload: DeviceResultPayload = {
      warranty_status: v.warranty_status,
      condition: v.condition,
      disposition: v.disposition,
      responsible_party: v.responsible_party,
      defect_type: v.defect_type,
      housing: v.housing ? Number(v.housing.value) : null,
      manufactured_at: v.manufactured_at || null,
      engineer_note: v.engineer_note.trim(),
      // причина: ровно один источник, второй обнуляем
      fault_reason: !v.manualReason && v.fault_reason ? Number(v.fault_reason.value) : null,
      fault_reason_text: v.manualReason && v.fault_reason_text.trim() ? v.fault_reason_text.trim() : null,
    };
    setSaving(true);
    try {
      const { data } = await diagnosticsApi.updateDevice(ticketId, device.id, payload);
      dispatch(setNotification('Результат по устройству сохранён'));
      onSaved(data);
    } catch (e: any) {
      dispatch(setErrorNotification(e?.response?.data?.detail || 'Не удалось сохранить результат'));
    } finally {
      setSaving(false);
    }
  });

  return (
    <MyModal
      isOpen={Boolean(device)}
      onClose={onClose}
      title={device ? `Результат: ${device.model_display}` : ''}
      description={device ? `Серийный номер: ${device.serial_number || 'б/н'}` : undefined}
      size="lg"
    >
      {device && (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <EnumField control={control} name="warranty_status" label="Гарантия" labels={WARRANTY_LABELS} />
            <EnumField control={control} name="condition" label="Состояние" labels={CONDITION_LABELS} />
            <EnumField control={control} name="disposition" label="Исход" labels={DISPOSITION_LABELS} />
            <EnumField control={control} name="responsible_party" label="Виновник" labels={RESPONSIBLE_LABELS} />
            <EnumField control={control} name="defect_type" label="Брак" labels={DEFECT_LABELS} />
            <div>
              <label className={FIELD_LABEL}>Корпус</label>
              <Controller
                control={control}
                name="housing"
                render={({ field }) => (
                  <Select
                    isVisible={false}
                    placeholder="—"
                    customStyles={selectStyles}
                    options={housingOptions}
                    optionSelected={field.value}
                    onSelected={(o) => field.onChange(o)}
                  />
                )}
              />
            </div>
            <div>
              <label className={FIELD_LABEL}>Дата производства</label>
              <input type="date" className={INPUT} {...register('manufactured_at')} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className={FIELD_LABEL}>Причина</label>
              {manualReason ? (
                <input className={INPUT} placeholder="Опишите причину" {...register('fault_reason_text')} />
              ) : (
                <Controller
                  control={control}
                  name="fault_reason"
                  render={({ field }) => (
                    <Select
                      isVisible={false}
                      placeholder="Выберите причину"
                      customStyles={selectStyles}
                      options={reasonOptions}
                      optionSelected={field.value}
                      onSelected={(o) => field.onChange(o)}
                    />
                  )}
                />
              )}
              <button
                type="button"
                className="mt-1 text-xs text-blue-600 hover:underline"
                onClick={() => {
                  setValue('manualReason', !manualReason);
                  setValue('fault_reason', null);
                  setValue('fault_reason_text', '');
                }}
              >
                {manualReason ? 'Выбрать из списка' : 'Указать вручную'}
              </button>
            </div>
          </div>

          <div>
            <label className={FIELD_LABEL}>Примечание инженера</label>
            <textarea
              className="w-full min-h-[80px] px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#2d2c2c] focus:outline-none focus:border-gray-400"
              {...register('engineer_note')}
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button isWhite type="button" onClick={onClose}>Отмена</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</Button>
          </div>
        </form>
      )}
    </MyModal>
  );
}
